import { useState } from 'react';
import { ApiError, login } from '../../api/client.js';
import { readStoredBackendBaseUrl, storeBackendBaseUrl } from '../../core/backend-url-store.js';

/**
 * Port of Android's `LoginScreen`/`LoginViewModel`.
 *
 * The server field mirrors the Android login screen's, down to the
 * persist-as-you-type behaviour: it is a "which backend am I pointing
 * at" setting, not something with validation-before-save semantics.
 * Prefilled from `localStorage`, falling back to the deployment default
 * the BFF reports. It is the only moment the API target can be chosen —
 * the BFF freezes it into the session at login (see
 * `src/server/backend-url.ts`).
 *
 * There is no in-app registration here either — the first parent account
 * is created with `POST /parents/register` against the backend directly
 * (see docs/DEVELOPMENT.md). That route is deliberately absent from the
 * BFF's allowlist: account bootstrap is not something the web app should
 * be able to do with a forged request.
 */
export function LoginPage({
  onLoggedIn,
  defaultBackendBaseUrl,
}: {
  onLoggedIn: () => void;
  defaultBackendBaseUrl: string;
}) {
  const [serverUrl, setServerUrl] = useState(
    () => readStoredBackendBaseUrl() ?? defaultBackendBaseUrl,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /** Thin sync wrapper — React wants a void-returning handler. */
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;
    void submitCredentials();
  }

  async function submitCredentials() {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await login(email.trim(), password, serverUrl.trim());
      onLoggedIn();
    } catch (error) {
      // The BFF already collapses "unknown account" and "wrong password"
      // into one message, mirroring the backend — nothing here tries to
      // be more specific.
      setErrorMessage(
        error instanceof ApiError ? error.message : 'Anmeldung fehlgeschlagen',
      );
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="page page--centered">
      <form className="card login" onSubmit={submit}>
        <h1 className="login__title">Parental Control</h1>
        <p className="muted">Elternzugang</p>

        <label className="field">
          <span>Server-URL</span>
          <input
            className="input"
            type="url"
            inputMode="url"
            value={serverUrl}
            onChange={(event) => {
              setServerUrl(event.target.value);
              // Persisted immediately rather than on submit, same as the
              // Android screen's onServerUrlChange.
              storeBackendBaseUrl(event.target.value);
            }}
            autoComplete="off"
            spellCheck={false}
            placeholder="https://api.example.org"
            required
          />
        </label>

        <label className="field">
          <span>E-Mail</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span>Passwort</span>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {errorMessage && (
          <p className="banner banner--error" role="alert">
            {errorMessage}
          </p>
        )}

        <button type="submit" className="button button--primary button--block" disabled={isSubmitting}>
          {isSubmitting ? 'Wird angemeldet…' : 'Anmelden'}
        </button>
      </form>
    </main>
  );
}
